import { useCallback, useState } from "react";

import { Box, IconButton, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import { isEqual } from "lodash";
import moment from "moment";
import { useUpdateEffect } from "react-use";

import DataGrid from "components/shared/XDataGrid";
import XNoRowsOverlay from "components/shared/XNoRowsOverlay";
import Pagination from "components/shared/Pagination";
import useAuth from "hooks/useAuth";
import useDataTable from "hooks/useXDataTable";
import { colors } from "theme";
import { ICustomPanel } from "types/panel";
import { UserRole } from "types/user";

import useDraftCustomPanels from "../../useDraftCustomPanel";
import { ActionsMenu } from "./ActionsMenu";

const useStyles = makeStyles((theme: Theme) => ({
  draftsTable: {
    backgroundColor: theme.palette.background.paper,
    border: "none",
    color: colors.nero[300],
    fontSize: 12,
    letterSpacing: 0.5,
    lineHeight: "17px",
  },
}));

export const DataTable = () => {
  const {
    currentPage,
    draftCustomPanels,
    fetch,
    loading,
    totalCount,
    setCurrentPage,
  } = useDraftCustomPanels();
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission([UserRole.Admin]);
  const columns: GridColDef[] = [
    {
      field: "modifiedAt",
      headerName: "Date Saved",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (params: GridValueFormatterParams) => {
        return moment(params.value as string).format("YYYY-MM-DD");
      },
    },
    {
      field: "name",
      headerName: "Panel Name",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "workflow",
      headerName: "Workflow",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      valueGetter: (params: GridValueFormatterParams) => {
        return (params.value as any)?.name;
      },
    },
    {
      field: "diseaseType",
      headerName: "Disease Type",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      valueGetter: (params: GridValueFormatterParams) => {
        return (params.value as any)?.name;
      },
    },
    {
      field: "owner",
      headerName: "Customer Name",
      flex: 1,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        `${params?.row?.owner?.firstName || ""} ${
          params?.row?.owner?.lastName || ""
        }` || "",
    },
    {
      field: "id",
      headerName: "More",
      width: 72,
      hideSortIcons: true,
      disableColumnMenu: true,
      headerAlign: "right",
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        return (
          <IconButton
            edge="start"
            color="primary"
            size="small"
            onClick={(event: React.MouseEvent<HTMLElement>) =>
              openMenu(event, params.row as ICustomPanel)
            }
          >
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
  ];

  const { CustomToolbar, filter, filterButtonEl, setFilter } = useDataTable({
    columns,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<ICustomPanel>(); // A state for quote id selected
  const classes = useStyles();

  useUpdateEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useUpdateEffect(() => {
    fetch(currentPage, filter.items.length > 0 ? filter.items[0] : undefined);
  }, [filter, currentPage]);

  const filterModelChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!isEqual(filter, filterModel?.items)) {
        setFilter({ ...filterModel });
      }
    },
    [filter, setFilter]
  );

  const openMenu = (
    event: React.MouseEvent<HTMLElement>,
    draftCustomPanel: ICustomPanel
  ) => {
    setSelectedDraft(draftCustomPanel);
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box mt={7}>
      <DataGrid
        autoHeight
        className={classes.draftsTable}
        components={{
          NoRowsOverlay: () => <XNoRowsOverlay text="No Draft Custom Panels" />,
          Pagination: () => (
            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          ),
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          panel: {
            anchorEl: filterButtonEl,
          },
        }}
        filterModel={filter}
        onFilterModelChange={filterModelChange}
        hideFooterSelectedRowCount
        hideFooterRowCount
        loading={loading}
        onPageChange={(page: number) => setCurrentPage(page)}
        page={currentPage - 1}
        pageSize={20}
        rowHeight={40}
        rows={draftCustomPanels}
        columns={columns}
        columnVisibilityModel={{ owner: isAdmin }}
      />
      {!!selectedDraft && (
        <ActionsMenu
          anchorEl={anchorEl}
          selectedDraft={selectedDraft}
          setAnchorEl={setAnchorEl}
        />
      )}
    </Box>
  );
};
